import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { numBlank, num } from './TimeReports';
import { useApp } from '../appContext';
import { MONTHS, forecastableProjects, projectDisplayName, round2 } from './ResourcePlanning';
import { useRememberedPeriod } from './rememberedPeriod';

// Same umbrella flag as My Time / Resource Planning / Time Reports -- this screen is the R&D side
// of the same forecast-vs-actuals picture.
const RESOURCES_ENABLED = process.env.REACT_APP_RESOURCES_ENABLED === 'true';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The two numbers a project row carries; this order is the row order in the grid.
export const RND_FIELDS = [
  { key: 'forecast', label: 'FCST' },
  { key: 'actual', label: 'ACT' },
];

// Shared cell styling, so the label column and the numbers can't drift apart row to row.
// verticalAlign because `.sheet td` is top-aligned by default, which left these labels floating
// above the inputs beside them.
const TRACK_CELL = {
  padding: '7px 10px', verticalAlign: 'middle', fontSize: 11, fontWeight: 600,
  letterSpacing: '0.04em', color: 'var(--text-mid)',
};
const TOTAL_CELL = { verticalAlign: 'middle', fontWeight: 600, color: 'var(--text-dark)' };
// Heavier than a project band on purpose -- otherwise the roll-up reads as a fourth project.
const FOOTER_BAND = {
  padding: '7px 10px', background: 'var(--light-bg)', color: 'var(--heading)',
  fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
  borderTop: '2px solid var(--text-light)',
};

// ---------- Pure helpers (exported for tests — see RndTimeEntry.test.js) ----------

// One row per project that either has R&D hours this year or was added to the grid this session.
// Projects with no hours are deliberately not listed: R&D works on a handful of projects, so an
// all-projects grid would be mostly empty.
export function buildRndRows(entries, projects, year, extraProjectIds = []) {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const rows = new Map();
  const ensure = (projectId) => {
    if (!rows.has(projectId)) {
      rows.set(projectId, {
        projectId,
        projectName: projectDisplayName(byId.get(projectId)) || 'Unknown project',
        forecast: {},
        actual: {},
        notes: {},
      });
    }
    return rows.get(projectId);
  };
  for (const entry of entries) {
    if (entry.year !== year) continue;
    const row = ensure(entry.projectId);
    row.forecast[entry.month] = entry.forecastHours;
    row.actual[entry.month] = entry.actualHours;
    if (entry.notes) row.notes[entry.month] = entry.notes;
  }
  for (const projectId of extraProjectIds) if (byId.has(projectId)) ensure(projectId);
  return [...rows.values()].sort((a, b) => a.projectName.localeCompare(b.projectName));
}

export function rndRowTotal(row, field) {
  return round2(MONTHS.reduce((sum, month) => sum + (row[field][month] || 0), 0));
}

export function rndColumnTotal(rows, field, month) {
  return round2(rows.reduce((sum, row) => sum + (row[field][month] || 0), 0));
}

// A cell's live value: the draft being typed if there is one, otherwise what was saved.
export function rndCellValue(drafts, row, field, month) {
  const draft = drafts[`${row.projectId}:${field}:${month}`];
  if (draft !== undefined) return draft;
  const saved = row[field][month];
  return saved == null ? '' : String(saved);
}

// Actual minus forecast, signed the way FCST vs Actuals signs it: positive = more hours burned
// than planned.
export function monthVariance(rows, month) {
  return round2(rndColumnTotal(rows, 'actual', month) - rndColumnTotal(rows, 'forecast', month));
}

export function totalVariance(rows) {
  return round2(MONTHS.reduce((sum, month) => sum + monthVariance(rows, month), 0));
}

function fmtSigned(n) {
  return n ? (n > 0 ? `+${num(n)}` : `${num(n)}`) : '';
}

// Green over forecast, red under -- the same reading as the report (Jeremy, 2026-08-09): the risk
// being watched for is work that was planned and never delivered.
function varianceStyle(value) {
  return {
    ...TOTAL_CELL,
    color: value ? (value > 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-light)',
  };
}

export function dirtyProjectIds(drafts) {
  return [...new Set(Object.keys(drafts).map((key) => key.split(':')[0]))];
}

// Both numbers travel together per month (they are one DB row), so a month with only its FCST
// edited still has to carry the saved ACT or the save would zero it.
export function buildRndPayload(drafts, rows, year) {
  const byId = new Map(rows.map((row) => [row.projectId, row]));
  const touched = new Map();
  for (const key of Object.keys(drafts)) {
    const [projectId, , month] = key.split(':');
    if (!touched.has(projectId)) touched.set(projectId, new Set());
    touched.get(projectId).add(Number(month));
  }
  const payloadRows = [];
  for (const [projectId, months] of touched) {
    const row = byId.get(projectId);
    if (!row) continue;
    const cells = [...months].sort((a, b) => a - b).map((month) => {
      const value = (field) => {
        const draft = drafts[`${projectId}:${field}:${month}`];
        const raw = draft !== undefined ? draft : row[field][month];
        const num = Number(raw);
        return raw === '' || raw == null || Number.isNaN(num) ? 0 : round2(num);
      };
      return { year, month, forecastHours: value('forecast'), actualHours: value('actual') };
    });
    if (cells.length > 0) payloadRows.push({ projectId, cells });
  }
  return { rows: payloadRows };
}

// ---------- Page ----------

function RndTimeEntry() {
  const { permissions } = useApp();
  const canEdit = !!permissions?.canEditRnd;
  const [year, setYear] = useRememberedPeriod('rndTimeEntry.year', new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [addedProjectIds, setAddedProjectIds] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entryRows, projectRows] = await Promise.all([
        apiFetch(`/rnd-entries?year=${year}`),
        apiFetch('/projects'),
      ]);
      setEntries(entryRows || []);
      setProjects(projectRows || []);
      setDrafts({});
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(
    () => buildRndRows(entries, projects, year, addedProjectIds),
    [entries, projects, year, addedProjectIds],
  );
  const dirtyIds = useMemo(() => dirtyProjectIds(drafts), [drafts]);
  const addable = useMemo(() => {
    const shown = new Set(rows.map((row) => row.projectId));
    return forecastableProjects(projects).filter((p) => !shown.has(p.id));
  }, [projects, rows]);

  function setCell(projectId, field, month, value) {
    setDrafts((prev) => ({ ...prev, [`${projectId}:${field}:${month}`]: value }));
    setSavedMessage('');
  }

  async function save() {
    setSaving(true);
    try {
      const payload = buildRndPayload(drafts, rows, year);
      if (payload.rows.length > 0) {
        await apiFetch('/rnd-entries', { method: 'PUT', body: JSON.stringify(payload) });
      }
      setSavedMessage(`Saved ${payload.rows.length} project${payload.rows.length === 1 ? '' : 's'}.`);
      setAddedProjectIds([]);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeRow(row) {
    // A row added but never saved exists only in this session, so it needs no round trip.
    if (!entries.some((entry) => entry.projectId === row.projectId && entry.year === year)) {
      setAddedProjectIds((prev) => prev.filter((id) => id !== row.projectId));
      return;
    }
    if (!window.confirm(`Remove ${row.projectName} from ${year}? Its R&D hours for that year are deleted.`)) return;
    try {
      await apiFetch(`/rnd-entries/${row.projectId}?year=${year}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (!RESOURCES_ENABLED) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">🔬</div>
        <div className="placeholder-title">R&amp;D Time Entry</div>
        <div className="placeholder-sub">
          Built, but not switched on in this environment. Set{' '}
          <code>REACT_APP_RESOURCES_ENABLED=true</code> to enable it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Resourcing</div>
        <h1 className="page-title">R&amp;D Time Entry</h1>
        <p className="page-subtitle">
          R&amp;D forecast and actual hours, one total per project per month — no timesheets. These
          hours stay out of PS forecasts and actuals unless a report is asked to include them.
        </p>
      </div>

      {error && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Year</label>
          <input
            type="number"
            className="form-input"
            style={{ width: 90 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value) || year)}
          />
        </div>
        {canEdit && (
          <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
            <label className="form-label">Add a project</label>
            <select
              className="form-select"
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                setAddedProjectIds((prev) => [...prev, e.target.value]);
              }}
            >
              <option value="">Select a project…</option>
              {addable.map((p) => <option key={p.id} value={p.id}>{projectDisplayName(p)}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            {!canEdit && (
              <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                Read-only — R&amp;D hours are entered by R&amp;D leadership and admins.
              </span>
            )}
            {savedMessage && <span style={{ fontSize: 12, color: 'var(--green)' }}>✓ {savedMessage}</span>}
            <div style={{ flex: 1 }} />
            {canEdit && (
              <>
                <span style={{ fontSize: 12, color: dirtyIds.length ? 'var(--heading)' : 'var(--text-mid)' }}>
                  {dirtyIds.length
                    ? `${dirtyIds.length} project${dirtyIds.length === 1 ? '' : 's'} changed`
                    : 'No changes'}
                </span>
                <button className="btn btn-outline" disabled={!dirtyIds.length || saving} onClick={() => setDrafts({})}>
                  Discard
                </button>
                <button className="btn btn-primary" disabled={!dirtyIds.length || saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="card">
              No R&amp;D hours recorded for {year} yet.{canEdit ? ' Add a project above to start.' : ''}
            </div>
          ) : (
            <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
              <table className="sheet">
                <thead>
                  <tr>
                    <th style={{ width: 74, minWidth: 74 }}>Track</th>
                    {MONTH_NAMES.map((m) => (
                      <th key={m} className="cell-month" style={{ textAlign: 'center', width: 56 }}>{m}</th>
                    ))}
                    <th className="cell-month" style={{ textAlign: 'center', width: 76 }}>Total</th>
                  </tr>
                </thead>
                {/* One <tbody> per project, each opening with a full-width name band: the project is
                    then a block you can see the edges of, and the FCST/ACT labels sit in a column of
                    their own instead of sharing one with the name. */}
                {rows.map((row) => (
                  <tbody key={row.projectId}>
                    <tr className="is-group">
                      <td colSpan={MONTHS.length + 2} style={{ borderTop: '2px solid var(--border)' }}>
                        {row.projectName}
                        {canEdit && (
                          <button
                            className="btn btn-outline"
                            style={{ marginLeft: 10, fontSize: 10, padding: '2px 7px' }}
                            onClick={() => removeRow(row)}
                            title={`Remove ${row.projectName} from ${year}`}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                    {RND_FIELDS.map((field) => (
                      <tr key={field.key}>
                        <td style={TRACK_CELL}>{field.label}</td>
                        {MONTHS.map((month) => (
                          <td key={month} className="cell-num cell-month" style={{ padding: canEdit ? 0 : '7px 4px' }}>
                            {canEdit ? (
                              <input
                                type="number"
                                min="0"
                                step="1"
                                aria-label={`${row.projectName} ${field.label} ${MONTH_NAMES[month - 1]}`}
                                value={rndCellValue(drafts, row, field.key, month)}
                                onChange={(e) => setCell(row.projectId, field.key, month, e.target.value)}
                                title={row.notes[month] || undefined}
                                style={{
                                  width: '100%', textAlign: 'center', border: '1px solid transparent',
                                  background: 'transparent', font: 'inherit', fontSize: 12,
                                  fontWeight: field.key === 'forecast' ? 600 : 400,
                                  padding: '7px 2px', borderRadius: 3,
                                }}
                              />
                            ) : (
                              row[field.key][month] ?? ''
                            )}
                          </td>
                        ))}
                        <td className="cell-num cell-month" style={TOTAL_CELL}>
                          {numBlank(rndRowTotal(row, field.key))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
                <tfoot>
                  <tr className="is-total">
                    <td colSpan={MONTHS.length + 2} style={FOOTER_BAND}>All projects — {year}</td>
                  </tr>
                  {RND_FIELDS.map((field) => (
                    <tr key={field.key}>
                      <td style={TRACK_CELL}>{field.label}</td>
                      {MONTHS.map((month) => (
                        <td key={month} className="cell-num cell-month" style={TOTAL_CELL}>
                          {numBlank(rndColumnTotal(rows, field.key, month))}
                        </td>
                      ))}
                      <td className="cell-num cell-month" style={{ ...TOTAL_CELL, fontWeight: 700 }}>
                        {numBlank(round2(rows.reduce((sum, r) => sum + rndRowTotal(r, field.key), 0)))}
                      </td>
                    </tr>
                  ))}
                  {/* Signed the same way FCST vs Actuals signs it: positive means more hours burned
                      than planned. */}
                  <tr title="Actual minus forecast — positive means more hours than planned">
                    <td style={TRACK_CELL}>VAR</td>
                    {MONTHS.map((month) => (
                      <td key={month} className="cell-num cell-month" style={varianceStyle(monthVariance(rows, month))}>
                        {fmtSigned(monthVariance(rows, month))}
                      </td>
                    ))}
                    <td className="cell-num cell-month" style={varianceStyle(totalVariance(rows))}>
                      {fmtSigned(totalVariance(rows))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RndTimeEntry;
